import { isDropped } from '../action/dispatch';
import { BAG_FULL_MESSAGE } from '../game-api';

/**
 * What to do with a batch of ground drops after the claim request comes back.
 *
 * This is pulled out of the farm frame loop on purpose: the loop runs inside Pixi
 * with a live camera and a real request, none of which jsdom can drive, and every
 * bug this file exists to prevent was a decision bug, not a rendering one.
 */
export interface PickupOutcome {
  /** Gone from the world: banked by us, or already gone (raced / rotted). */
  remove: string[];
  /** Untouched — put back in play so a later frame retries. */
  release: string[];
  /** Latched message for the `drops.full` notifyOnce key (null clears it). */
  fullMessage: string | null;
}

/**
 * A sweep that hits a full bag still banked everything before it and returns those
 * in `collected`. Only the ones it never got to go back on the ground.
 *
 * Treating the whole batch as failed — what the old error path did, because the
 * server threw the body away — made banked drops reappear, left the bag/HUD stale,
 * and had the next frame re-claim them into 'Already picked up' forever.
 */
export function settlePickup(
  ids: string[],
  res: { collected: { id: string }[]; bagFull: boolean },
): PickupOutcome {
  const taken = new Set(res.collected.map((d) => d.id));
  const out: PickupOutcome = { remove: [], release: [], fullMessage: res.bagFull ? BAG_FULL_MESSAGE : null };
  for (const id of ids) {
    if (!res.bagFull || taken.has(id)) out.remove.push(id);
    else out.release.push(id);
  }
  return out;
}

/**
 * The toast for a failed claim, or null for "say nothing".
 *
 * Two bugs live here. `isDropped` must be checked FIRST: the dispatcher rejects
 * with ActionDropped('action dropped: in-flight'|'rate-limited') for its own
 * debouncing, which is not a player-visible failure and which a message match on
 * "bag is full" would misread — that misread also cleared the full-bag latch, so
 * standing on a pile alternated rejections and re-toasted every frame.
 *
 * And everything else must produce a message: offline, 500 and 429 previously fell
 * into an else-branch that toasted nothing, reintroducing the silent failure this
 * whole change set out to remove.
 */
export function pickupFailureMessage(e: unknown): string | null {
  if (isDropped(e)) return null;
  return e instanceof Error && e.message ? e.message : 'Could not pick that up';
}
